// ---------------------------------------------------------------------------
// Sophia error log — one place where every fault lands
//
// Faults used to vanish into the console: a failed relay probe, a rejected
// cloud upsert, a thrown render, a 500 from a server function. Nothing kept
// them, so by the time anyone looked the evidence was gone. This module is the
// black box: it captures uncaught errors, unhandled promise rejections,
// anything routed through console.error/console.warn, and failed network
// calls, then keeps them in a de-duplicated ring buffer on the device so they
// can be read, copied and fixed later.
//
// Everything is local-only and best-effort — the logger must never be the
// thing that breaks the app, so every path is wrapped.
// ---------------------------------------------------------------------------

export type ErrorLevel = "error" | "warn" | "info";

export interface ErrorEntry {
  /** Stable signature id — repeats of the same fault increment `count`. */
  id: string;
  /** First time this fault was seen. */
  firstAt: number;
  /** Most recent time this fault was seen. */
  at: number;
  level: ErrorLevel;
  /** Where it came from: "uncaught", "promise", "console", "network", or a module name. */
  source: string;
  message: string;
  stack?: string;
  /** Route/page the fault happened on. */
  route?: string;
  /** Extra structured context (status codes, ids, payload hints). */
  detail?: Record<string, unknown>;
  /** How many times this exact fault has happened. */
  count: number;
}

import { pruneByRetention } from "./diagRetention";

const KEY = "sophia:error-log";
const MAX = 300;

type Listener = (entries: ErrorEntry[]) => void;
const listeners = new Set<Listener>();

let cache: ErrorEntry[] | null = null;

function read(): ErrorEntry[] {
  if (cache) return cache;
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
    const parsed = raw ? (JSON.parse(raw) as ErrorEntry[]) : [];
    cache = Array.isArray(parsed) ? pruneByRetention(parsed, (e) => e?.at ?? 0) : [];
  } catch { cache = []; }
  return cache;
}

function write(entries: ErrorEntry[]) {
  cache = entries.slice(0, MAX);
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* quota — keep memory copy */ }
  listeners.forEach((l) => { try { l(cache!); } catch { /* ignore */ } });
}

function signature(source: string, message: string): string {
  const base = `${source}|${message}`.toLowerCase().replace(/\d{3,}/g, "#").slice(0, 200);
  let h = 0;
  for (let i = 0; i < base.length; i++) h = (h * 31 + base.charCodeAt(i)) | 0;
  return `e${Math.abs(h).toString(36)}`;
}

function currentRoute(): string | undefined {
  try { return typeof location !== "undefined" ? location.pathname + location.hash : undefined; }
  catch { return undefined; }
}

function clip(s: unknown, n = 600): string {
  const t = typeof s === "string" ? s : safeStringify(s);
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v); } catch { return String(v); }
}

/** Turn anything that was thrown into a message + stack. */
export function describeError(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) {
    const out: { message: string; stack?: string } = { message: err.message || err.name };
    if (err.stack) out.stack = clip(err.stack, 1200);
    return out;
  }
  if (typeof err === "string") return { message: err };
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    const msg = typeof o['message'] === "string" ? o['message'] : safeStringify(o);
    return { message: clip(msg, 400) };
  }
  return { message: String(err) };
}

// ---------------------------------------------------------------------------
// Noise
//
// The report is only useful if every line in it is a real fault. Editor and
// preview tooling, browser autoplay scolding and GPU performance chatter all
// arrive through console.warn and were filling the diagnostics report with
// things nobody can act on — burying the faults that matter. These are dropped
// before they ever reach the black box.
// ---------------------------------------------------------------------------
const NOISE: RegExp[] = [
  /unknown message type/i,
  /lovable-preview-auth/i,
  /reset_blank_check/i,
  /^\[vite\]/i,
  /hot updated|hmr update|websocket connection to .*__vite|server connection lost/i,
  /download the react devtools|react devtools/i,
  /the audiocontext was not allowed to start|autoplay policy/i,
  /webgl|gl driver message|groupmarkernotset|swiftshader|gpu stall/i,
  /resizeobserver loop/i,
  /non-passive event listener|will-change memory consumption/i,
  /third-party cookie|partitioned cookie|deprecationwarning/i,
  /chrome-extension:\/\/|moz-extension:\/\//i,
  /^\s*$/,
];

/** True when a message is tooling chatter rather than a fault worth reporting. */
export function isNoise(message: string, source?: string): boolean {
  if (source && /^(console|asset)$/.test(source) === false) {
    // Faults our own modules report by name are always kept.
    if (source !== "network" && source !== "uncaught" && source !== "promise") return false;
  }
  return NOISE.some((re) => re.test(message));
}

/** Record a fault. Safe to call from anywhere, including inside catch blocks. */
export function logError(
  source: string,
  err: unknown,
  detail?: Record<string, unknown>,
  level: ErrorLevel = "error",
): ErrorEntry | null {
  try {
    const { message, stack } = describeError(err);
    if (!message) return null;
    if (isNoise(message, source)) return null;
    const id = signature(source, message);
    const entries = read().slice();
    const now = Date.now();
    const existing = entries.find((e) => e.id === id);
    if (existing) {
      existing.count += 1;
      existing.at = now;
      if (detail) existing.detail = detail;
      if (stack) existing.stack = stack;
      // keep most-recent-first ordering
      write([existing, ...entries.filter((e) => e.id !== id)]);
      return existing;
    }
    const entry: ErrorEntry = {
      id, firstAt: now, at: now, level, source,
      message: clip(message, 500), count: 1,
      ...(stack ? { stack } : {}),
      ...(currentRoute() ? { route: currentRoute() } : {}),
      ...(detail ? { detail } : {}),
    };
    write([entry, ...entries]);
    return entry;
  } catch { return null; }
}

export function logWarning(source: string, err: unknown, detail?: Record<string, unknown>) {
  return logError(source, err, detail, "warn");
}

export function listErrors(): ErrorEntry[] { return read(); }
export function errorCount(level?: ErrorLevel): number {
  const all = read();
  return (level ? all.filter((e) => e.level === level) : all).reduce((n, e) => n + e.count, 0);
}
export function clearErrors(): void { write([]); }
export function dismissError(id: string): void { write(read().filter((e) => e.id !== id)); }
export function subscribeErrors(fn: Listener): () => void {
  listeners.add(fn);
  try { fn(read()); } catch { /* ignore */ }
  return () => { listeners.delete(fn); };
}

/** A pasteable report of everything logged — for bug reports and for her own
 *  self-repair passes. */
export function formatErrorLog(entries: ErrorEntry[] = read()): string {
  const head = [
    "Sophia error log",
    `exported: ${new Date().toISOString()}`,
    `entries: ${entries.length} · occurrences: ${entries.reduce((n, e) => n + e.count, 0)}`,
    typeof navigator !== "undefined" ? `agent: ${navigator.userAgent}` : "",
    "",
  ].filter(Boolean);
  const body = entries.map((e) => {
    const parts = [
      `[${e.level}] ${e.source} ×${e.count}`,
      `  when: ${new Date(e.firstAt).toISOString()} → ${new Date(e.at).toISOString()}`,
      e.route ? `  route: ${e.route}` : "",
      `  message: ${e.message}`,
      e.detail ? `  detail: ${safeStringify(e.detail)}` : "",
      e.stack ? `  stack: ${e.stack}` : "",
    ].filter(Boolean);
    return parts.join("\n");
  });
  return [...head, ...body].join("\n");
}

export function downloadErrorLog(): void {
  try {
    const blob = new Blob([formatErrorLog()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sophia-errors-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { logError("errorLog.download", e); }
}

// ---------------------------------------------------------------------------
// Global capture
// ---------------------------------------------------------------------------

let bound = false;

/** Attach the global listeners. Idempotent, browser-only. */
export function bindErrorLog(): void {
  if (bound || typeof window === "undefined") return;
  bound = true;

  // Anything the old, unfiltered logger already stored gets swept out once, so
  // the diagnostics report is clean the first time you open it after this.
  try {
    const kept = read().filter((e) => !isNoise(e.message, e.source));
    if (kept.length !== read().length) write(kept);
  } catch { /* ignore */ }

  window.addEventListener("error", (ev) => {
    const e = ev as ErrorEvent;
    // Asset/media load failures arrive as error events on the element itself.
    const target = ev.target as HTMLElement | null;
    if (target && target !== (window as unknown as HTMLElement) && "tagName" in target) {
      const src = (target as HTMLImageElement).src || (target as HTMLScriptElement).src || "";
      if (src) {
        logWarning("asset", `failed to load ${target.tagName.toLowerCase()}`, { src: clip(src, 200) });
        return;
      }
    }
    logError("uncaught", e.error ?? e.message, {
      file: clip(e.filename ?? "", 200), line: e.lineno, col: e.colno,
    });
  }, true);

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = (ev as PromiseRejectionEvent).reason;
    // An abort is a cancelled request, not a fault: a single slow news cycle
    // used to fill the black box with hundreds of them and trip safe mode.
    const name = reason instanceof Error ? reason.name : "";
    if (name === "AbortError" || name === "TimeoutError") {
      logWarning("network", reason);
      return;
    }
    logError("promise", reason);
  });

  // Anything Sophia's modules already shout about goes in the log too, without
  // silencing the console itself.
  patchConsole("error", "error");
  patchConsole("warn", "warn");
  patchFetch();
}

function patchConsole(method: "error" | "warn", level: ErrorLevel) {
  try {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      try {
        const first = args[0];
        const message = args.map((a) => (typeof a === "string" ? a : describeError(a).message)).join(" ");
        if (message && !message.startsWith("[sophia-log]")) {
          logError("console", first instanceof Error ? first : message, undefined, level);
        }
      } catch { /* never break logging */ }
      original(...args);
    };
  } catch { /* ignore */ }
}

function patchFetch() {
  try {
    if (typeof window.fetch !== "function") return;
    const original = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      try {
        const res = await original(input as RequestInfo, init);
        if (!res.ok && res.status >= 500) {
          logWarning("network", `${res.status} ${res.statusText || "server error"}`, { url: clip(url, 200) });
        }
        return res;
      } catch (e) {
        // Aborted requests are normal (navigation, timeouts we control).
        const name = (e as Error)?.name;
        if (name !== "AbortError" && !/[?&]ping=/.test(url)) logWarning("network", e, { url: clip(url, 200) });
        throw e;
      }
    };
  } catch { /* ignore */ }
}
